import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  BarChart3, Users, Building2, Settings, 
  TrendingUp, Bell, ChevronDown, ChevronRight,
  Activity, Shield, Database, Brain, Zap, MessageSquare, HardDrive, Link, Sparkles
} from 'lucide-react';

interface ImprovedAdminNavigationProps {
  isSuperAdmin: boolean;
  isFirmaAdmin: boolean;
  isFirmaSuperAdmin: boolean;
}

interface MenuGroup {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  path?: string;
  items: MenuItem[];
  roles?: string[];
}

interface MenuItem {
  path: string;
  title: string;
  icon: React.ComponentType<any>;
  roles?: string[];
}

const ImprovedAdminNavigation: React.FC<ImprovedAdminNavigationProps> = ({ 
  isSuperAdmin, 
  isFirmaAdmin,
  isFirmaSuperAdmin 
}) => {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const menuGroups: MenuGroup[] = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: BarChart3,
      path: '/admin/dashboard',
      items: []
    },
    {
      id: 'users',
      title: 'Kullanıcı Yönetimi',
      icon: Users,
      roles: ['ekostrateji_super_admin'],
      items: [
        { path: '/admin/users', title: 'Kullanıcı İşlemleri', icon: Users },
        { path: '/admin/users/new', title: 'Yeni Kullanıcı', icon: Users },
        { path: '/admin/demo-requests', title: 'Demo Talepleri', icon: Sparkles }
      ]
    },
    {
      id: 'companies',
      title: 'Firma Yönetimi',
      icon: Building2,
      roles: ['ekostrateji_super_admin'],
      items: [
        { path: '/admin/companies', title: 'Firma Listesi', icon: Building2 },
        { path: '/admin/company-management', title: 'Logo Yönetimi', icon: Settings }
      ]
    },
    {
      id: 'system',
      title: 'Sistem Yönetimi',
      icon: Settings,
      roles: ['ekostrateji_super_admin'],
      items: [
        { path: '/admin/rag-management', title: 'RAG Yönetimi', icon: Database },
        { path: '/admin/cache-management', title: 'Cache Yönetimi', icon: Zap },
        { path: '/admin/learning-management', title: 'Learning Yönetimi', icon: Brain },
        { path: '/admin/embedding-management', title: 'Embedding Yönetimi', icon: Database },
        { path: '/admin/security-settings', title: 'Güvenlik Ayarları', icon: Shield },
        { path: '/admin/system-health', title: 'Sistem Sağlığı', icon: Activity },
        { path: '/admin/learning-analytics', title: 'Learning Analytics', icon: TrendingUp },
        { path: '/admin/backup-management', title: 'Yedekleme Yönetimi', icon: HardDrive }
      ]
    },
    {
      id: 'analytics',
      title: 'Analitik & Raporlar',
      icon: TrendingUp,
      roles: ['ekostrateji_super_admin'],
      items: [
        { path: '/admin/activity-logs', title: 'Aktivite Logları', icon: Activity },
        { path: '/admin/page-analytics', title: 'Sayfa Analitikleri', icon: BarChart3 },
        { path: '/admin/system-analytics', title: 'Sistem Kullanımı', icon: TrendingUp },
        { path: '/admin/activity-analytics', title: '📊 Aktivite Analitikleri', icon: BarChart3 },
        { path: '/admin/realtime-monitor', title: '🔴 Gerçek Zamanlı Monitör', icon: Activity },
        { path: '/admin/dashboard-analytics', title: 'Dashboard Analitikleri', icon: BarChart3 },
        { path: '/admin/detailed-reports', title: 'Detaylı Raporlar', icon: TrendingUp }
      ]
    },
    {
      id: 'messaging',
      title: 'Mesajlaşma',
      icon: MessageSquare,
      roles: ['ekostrateji_super_admin', 'firma_super_admin', 'firma_admin'],
      items: [
        { path: '/admin/messages', title: 'Mesaj Kutusu', icon: MessageSquare },
        { path: '/admin/message-templates', title: 'Mesaj Şablonları', icon: Settings }
      ]
    },
    {
      id: 'notifications',
      title: 'Bildirim Yönetimi',
      icon: Bell,
      roles: ['ekostrateji_super_admin'],
      items: [
        { path: '/admin/notification-center', title: 'Bildirim Merkezi', icon: Bell },
        { path: '/admin/notification-history', title: 'Bildirim Geçmişi', icon: Activity },
        { path: '/admin/system-notifications', title: 'Sistem Hataları', icon: Shield }
      ]
    },
    {
      id: 'roles',
      title: 'Rol Yönetimi',
      icon: Shield,
      roles: ['ekostrateji_super_admin'],
      items: [
        { path: '/admin/role-management', title: 'Rol Dashboard', icon: Shield },
        { path: '/admin/permission-matrix', title: 'Yetki Matrisi', icon: Settings },
        { path: '/admin/role-definitions', title: 'Rol Tanımları', icon: Users },
        { path: '/admin/role-audit', title: 'Değişiklik Geçmişi', icon: Activity },
        { path: '/admin/role-rollback', title: 'Rollback Sistemi', icon: Settings },
        { path: '/admin/role-suggestions', title: 'Akıllı Öneriler', icon: TrendingUp },
        { path: '/admin/role-reports', title: 'Raporlama', icon: BarChart3 }
      ]
    },
    {
      id: 'sap',
      title: 'SAP Entegrasyon',
      icon: Link,
      roles: ['ekostrateji_super_admin', 'firma_super_admin', 'firma_admin'],
      items: [
        { path: '/admin/sap-settings', title: 'SAP Ayarları', icon: Settings },
        { path: '/admin/sap-sync-logs', title: 'Sync Logları', icon: Activity }
      ]
    }
  ];

  // Auto-expand based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    const newExpanded = new Set<string>();
    
    menuGroups.forEach(group => {
      const hasActiveItem = group.items.some(item => currentPath === item.path);
      if (hasActiveItem) {
        newExpanded.add(group.id);
      }
    });
    
    setExpandedGroups(newExpanded);
  }, [location.pathname]);

  const hasRoleAccess = (roles?: string[]) => {
    if (!roles) return true;
    if (isSuperAdmin && roles.includes('ekostrateji_super_admin')) return true;
    if (isFirmaSuperAdmin && roles.includes('firma_super_admin')) return true;
    if (isFirmaAdmin && roles.includes('firma_admin')) return true;
    return false;
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  return (
    <div className="admin-nav-improved">
      {menuGroups.map(group => {
        if (!hasRoleAccess(group.roles)) return null;
        
        const isExpanded = expandedGroups.has(group.id);

        // Eğer path varsa direkt link, yoksa expandable grup
        if (group.path) {
          return (
            <NavLink
              key={group.id}
              to={group.path}
              className={({ isActive }) => `admin-group-header admin-direct-link ${isActive ? 'active' : ''}`}
            >
              <group.icon className="admin-group-icon" size={18} />
              <span className="admin-group-title">{group.title}</span>
            </NavLink>
          );
        }

        return (
          <div key={group.id} className={`admin-menu-group ${isExpanded ? 'expanded' : ''}`}>
            <div 
              className="admin-group-header"
              onClick={() => toggleGroup(group.id)}
            >
              <group.icon className="admin-group-icon" size={18} />
              <span className="admin-group-title">{group.title}</span>
              <div className="admin-group-chevron">
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>
            </div>
            
            {isExpanded && (
              <div className="admin-group-items">
                {group.items.map(item => {
                  if (!hasRoleAccess(item.roles)) return null;
                  
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end
                      className={({ isActive }) => `admin-group-item ${isActive ? 'active' : ''}`}
                    >
                      <item.icon className="admin-item-icon" size={16} />
                      <span className="admin-item-title">{item.title}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ImprovedAdminNavigation;
